import { inject, Injectable } from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  forkJoin,
  map,
  mergeMap,
  Observable,
  shareReplay,
  switchMap,
  catchError,
  of,
  filter,
} from 'rxjs';
import {
  CalendarType,
  HostService,
  newBusinessCenterApplicationContextProperties,
  newPartnerCenterApplicationContextProperties,
} from '@vendasta/meetings';
import { CalendarWithMeetingTypes } from '../interfaces/config.interface';
import { SalespersonService, SalesTeamService } from '@vendasta/sales';

import { ACCOUNT_GROUP_ID_TOKEN, CURRENT_USER_ID_TOKEN, PARTNER_ID_TOKEN } from '../tokens';

@Injectable()
export class BookingConfigService {
  private readonly currentUserId$ = inject(CURRENT_USER_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);

  private readonly meetingService = inject(HostService);
  private readonly salespersonService = inject(SalespersonService);
  private readonly salesTeamService = inject(SalesTeamService);

  private readonly partnerIdAndAccountGroupId$ = combineLatest([this.partnerId$, this.accountGroupId$]).pipe(
    distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
  );

  private readonly salesPersonId$ = combineLatest([this.partnerId$, this.currentUserId$]).pipe(
    switchMap(([partnerId, userId]) => this.salespersonService.getSalespersonByUserId(partnerId, userId)),
    catchError(() => of(undefined)),
    map((salesperson) => salesperson?.salespersonId || ''),
    shareReplay(1),
  );

  private readonly salesTeamIDs$ = this.salesPersonId$.pipe(
    switchMap((id) => (id ? this.salesTeamService.getSalesTeamsBySalesperson(id) : of([]))),
    catchError(() => of([])),
    map((teams) => teams.map((team) => team?.groupId).filter((groupId): groupId is string => groupId !== undefined)),
  );

  private readonly personalCalendar$ = combineLatest([this.partnerIdAndAccountGroupId$, this.currentUserId$]).pipe(
    switchMap(([[partnerId, accountGroupId], userId]) => {
      if (accountGroupId) {
        return this.meetingService.ensurePersonalCalendarExists({
          applicationContextProperties: newBusinessCenterApplicationContextProperties({ business_id: accountGroupId }),
          userId: userId,
        });
      } else {
        return this.salesPersonId$.pipe(
          filter((salespersonId) => !!salespersonId),
          switchMap(() =>
            this.meetingService.ensurePersonalCalendarExists({
              applicationContextProperties: newPartnerCenterApplicationContextProperties({ partner_id: partnerId }),
              userId: userId,
            }),
          ),
        );
      }
    }),
    switchMap((calendarId) => this.meetingService.getCalendar({ calendarId: calendarId })),
  );

  private readonly groupCalendars$ = this.partnerIdAndAccountGroupId$.pipe(
    switchMap(([_, accountGroupId]) => {
      if (accountGroupId) {
        return this.meetingService.ensureGroupCalendarsExist({ externalIds: [accountGroupId] });
      } else {
        return this.salesTeamIDs$.pipe(
          switchMap((salesTeamIds) =>
            salesTeamIds.length === 0
              ? of([])
              : this.meetingService.ensureGroupCalendarsExist({
                  externalIds: salesTeamIds,
                }),
          ),
        );
      }
    }),
    mergeMap((calendarIds) => {
      if (calendarIds.length === 0) {
        return of([]);
      }
      return forkJoin(calendarIds.map((calendarId) => this.meetingService.getCalendar({ calendarId })));
    }),
  );

  private readonly allCalendars$ = combineLatest([this.personalCalendar$, this.groupCalendars$]).pipe(
    map(([personalCalendar, groupCalendars]) => [personalCalendar, ...groupCalendars]),
    shareReplay(1),
  );

  private readonly calendarEvents$ = this.allCalendars$.pipe(
    switchMap((calendars) => {
      const calendarIds = calendars.map((calendar) => calendar.id);

      return this.meetingService.getMeetingTypesForCalendars({
        calendarIds: calendarIds,
        metadata: {},
      });
    }),
  );

  getCalendarsWithTypes(): Observable<CalendarWithMeetingTypes[]> {
    return combineLatest([this.allCalendars$, this.calendarEvents$]).pipe(
      map(([calendars, events]) =>
        calendars
          .filter((calendar) => calendar.calendarType !== CalendarType.CALENDAR_TYPE_INVALID)
          .map(
            (calendar) =>
              ({
                calendarName: calendar.displayName,
                calendarId: calendar.id,
                meetingTypes:
                  events?.[calendar.id]?.map((mt) => ({
                    name: mt.name,
                    meetingTypeId: mt.id,
                    calendarId: calendar.id,
                  })) || [],
                isPersonal: calendar.calendarType === CalendarType.CALENDAR_TYPE_PERSONAL,
              }) as CalendarWithMeetingTypes,
          )
          .sort((a, _) => (a.isPersonal ? -1 : 1)),
      ),
    );
  }
}
